import React, {type ReactElement} from "react";
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import type {
    Trigger,
    TriggerInput,
    TriggerRow,
    TriggerRowInput,
    Variable
} from "../../base/ui_general/GeneralInterfaces.tsx";
import {RBSCard, RBSCardVariants} from '../../base/rbs_components/RBSCard';
import {RBSButton, RBSButtonSizes, RBSButtonVariants} from '../../base/rbs_components/RBSButton';
import {Col, Row} from 'react-bootstrap';
import autobind from "autobind-decorator";
import {BaseComponent, type BaseComponentState} from "../../base/ui_general/BaseComponent.tsx";
import {
    ApiEndPoints,
    LabelTextMessageTypes,
    TriggerComparisonOperatorEnum,
    TriggerLogicalOperatorEnum,
    TriggerTypeEnum,
    ValidationSchemes,
    WaterfallProcessableTypeEnum,
} from "../../base/Settings.tsx";
import {getRequest_create_trigger, getRequest_update_trigger, getRequest_delete_trigger} from "../../api/trigger.tsx";
import {
    getRequest_create_trigger_row,
    getRequest_update_trigger_row,
    getRequest_delete_trigger_row
} from "../../api/trigger_row.tsx";
import {createSuccessFeedbackMessage} from '../../base/utils/GeneralUtilities';
import {InputStateControl} from "../../base/utils/InputStateControl.tsx";
import {TriggerEditModal} from "../modals/TriggersEditModal.tsx";
import {VariablesBadges} from "../../base/ui_general/GeneralFunctionComponents.tsx";
import {TabCardHeader} from './components/TabCardHeader';
import {EmptyStateRow} from './components/EmptyStateRow';
import {VariableDropCell} from './components/VariableDropCell';
import styles from './TabShared.module.css';


/**
 * Mapping of logical operators to display labels.
 */
const logicalOperatorLabelMap: { [key in TriggerLogicalOperatorEnum]: string } = {
    [TriggerLogicalOperatorEnum.AND]: "AND",
    [TriggerLogicalOperatorEnum.OR_BRACKETS]: ") OR ("
};


/**
 * Mapping of comparison operators to symbols.
 */
const operatorSymbolMap: { [key in TriggerComparisonOperatorEnum]: string } = {
    [TriggerComparisonOperatorEnum.GT]: ">",
    [TriggerComparisonOperatorEnum.GE]: "≥",
    [TriggerComparisonOperatorEnum.LT]: "<",
    [TriggerComparisonOperatorEnum.LE]: "≤",
    [TriggerComparisonOperatorEnum.EQUAL]: "=",
};


/**
 * Stores IDs for input states of a row, which need to be filled in correctly.
 */
interface RowInputStateIds {
    variableId: number;
    numberId: number;
}


/**
 * Props for the TriggersTab component.
 */
interface TriggersTabProps extends DesignCenterTabProps {
    triggers: Array<Trigger>;
    variables: Array<Variable>;
}


/**
 * State for the TriggersTab component.
 */
interface TriggersTabState extends BaseComponentState {
    localTriggers: Array<Trigger>;
    nextRowId: number;
    /** Currently hovered row id (undefined means none) */
    hoveredRow: string | undefined;
    draggedRow: { triggerId: string; rowId: string; variableName: string } | null;
    showEditTriggerModal: boolean;
    editingTrigger: Trigger | undefined;
    editingTriggerValid: boolean;
    draggedTriggerId: string | null;
    rowInputStateIds: Record<string, RowInputStateIds>;
}


/**
 * Component for managing triggers and their rows.
 */
export class TriggersTab extends BaseComponent<TriggersTabProps, TriggersTabState> {
    /**
     * Input state control instance for row inputs.
     */
    private rowInputStateControl = new InputStateControl();
    private editingTriggerInputControl: InputStateControl = new InputStateControl();


    /**
     * Constructor initializes state and sets up input control callbacks.
     */
    constructor(props: TriggersTabProps) {
        super(props);

        this.editingTriggerInputControl.setGlobalValidityUpdateCallback(
            (isValid: boolean) => this.setState({editingTriggerValid: isValid})
        );

        this.state = {
            ...this.state,
            localTriggers: props.triggers || [],
            nextRowId: 1,
            hoveredRow: undefined,
            draggedRow: null,
            showEditTriggerModal: false,
            editingTrigger: undefined,
            editingTriggerValid: true,
            draggedTriggerId: null,
            rowInputStateIds: {},
        };
    }


    /**
     * Registers input fields for a row in the input state control.
     */
    private registerRowInputs(row: TriggerRow) {
        const rowKey = row.id;
        if (!this.state.rowInputStateIds[rowKey]) {
            const variableId = this.rowInputStateControl.registerComponent(
                rowKey + "-variable",
                [ValidationSchemes.REQUIRED]
            );
            const numberId = this.rowInputStateControl.registerComponent(
                rowKey + "-number",
                [ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER]
            );
            this.rowInputStateControl.updateValue(
                variableId, row.variable_id || ""
            );
            this.rowInputStateControl.updateValue(
                numberId,
                (row.comparison_value !== undefined && row.comparison_value !== null)
                    ? String(row.comparison_value)
                    : ""
            );
            this.setState(state => ({
                rowInputStateIds: {
                    ...state.rowInputStateIds,
                    [rowKey]: {variableId, numberId}
                }
            }));
        }
    }

    /**
     * Updates local state when props change.
     * Registers any new rows that do not yet have input state IDs.
     */

    componentDidUpdate(prevProps: TriggersTabProps) {
        if (prevProps.triggers !== this.props.triggers) {
            const newRows: Array<TriggerRow> = [];
            (this.props.triggers || []).forEach(trigger => {
                (trigger.trigger_rows || []).forEach(row => {
                    if (!this.state.rowInputStateIds[row.id]) newRows.push(row);
                });
            });
            newRows.forEach(row => this.registerRowInputs(row));
            this.setState({localTriggers: this.props.triggers || []});
        }
    }

    /**
     * Sends an update request to the backend for a trigger.
     * @param updatedTrigger The trigger to update.
     */

    @autobind
    private sendUpdateTrigger(updatedTrigger: Trigger) {
        this.processFetchRequest(
            getRequest_update_trigger(updatedTrigger),
            () => {
                this.props.loadData([
                    ApiEndPoints.TRIGGER,
                    ApiEndPoints.VARIABLE
                ]);
                this.props.displayMessage?.(createSuccessFeedbackMessage(LabelTextMessageTypes.TRIGGER_UPDATED));
            }
        );
    }

    /**
     * Sends an update request to the backend for a trigger row.
     * @param updatedRow The trigger row to update.
     */

    @autobind
    private sendUpdateRow(updatedRow: TriggerRow) {
        this.processFetchRequest(
            getRequest_update_trigger_row(updatedRow),
            () => {
                this.props.loadData([ApiEndPoints.TRIGGER]);
                this.props.displayMessage?.(createSuccessFeedbackMessage(LabelTextMessageTypes.TRIGGER_UPDATED));
            }
        );
    }

    /**
     * Adds a new trigger and an initial auto-generated row.
     */

    @autobind
    async handleAddTrigger() {
        if (!this.props.transaction) return;

        const {localTriggers, nextRowId} = this.state;

        const baseName = "New Trigger";
        const regex = new RegExp(`^${baseName}(\\s(\\d+))?$`);
        let maxNum = 0;

        localTriggers.forEach(t => {
            const m = (t.name).match(regex);
            if (m) {
                const num = m[2] ? parseInt(m[2], 10) : 1;
                if (num > maxNum) maxNum = num;
            }
        });

        const newTriggerNumber = maxNum + 1;
        const newTriggerName = `${baseName} ${newTriggerNumber}`;

        const newTriggerPayload: Omit<Trigger, "id"> = {
            name: newTriggerName,
            description: "",
            trigger_type: TriggerTypeEnum.IRREVERSIBLE,
            processing_order: localTriggers.length + 1,
            trigger_rows: [],
            creation_timestamp: new Date().toISOString(),
            waterfall_processable_type: WaterfallProcessableTypeEnum.TRIGGER,
            transaction_id: this.props.transaction.id,
            trigger_variable_id: crypto.randomUUID()
        };
        this.processFetchRequest(
            getRequest_create_trigger(newTriggerPayload as Trigger),
            (createdTrigger: Trigger) => {
                if (!createdTrigger?.id) return;
                const initialRow: TriggerRow = {
                    id: `newrow-${nextRowId}`,
                    creation_timestamp: new Date().toISOString(),
                    logical_operator: TriggerLogicalOperatorEnum.AND,
                    comparison_operator: TriggerComparisonOperatorEnum.GT,
                    comparison_value: undefined,
                    trigger_id: createdTrigger.id,
                    processing_order: 1,
                    variable_id: undefined,
                };
                this.props.loadData([ApiEndPoints.TRIGGER, ApiEndPoints.VARIABLE]);
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.TRIGGER_CREATED));
                this.registerRowInputs(initialRow);
                this.setState(
                    (state) => ({
                        localTriggers: [
                            ...state.localTriggers,
                            {...createdTrigger, trigger_rows: [initialRow]}
                        ],
                        nextRowId: state.nextRowId + 1
                    })
                );
            }
        );
    }

    /**
     * Adds a new row to an existing trigger.
     * @param triggerId ID of the trigger to add the row to.
     */

    @autobind
    async handleAddTriggerRow(triggerId: string) {
        const {localTriggers, nextRowId} = this.state;
        const parentTrigger = localTriggers.find(t => t.id === triggerId);
        if (!parentTrigger) return;
        const tempId = `newrow-${nextRowId}`;
        const newRow: TriggerRow = {
            id: tempId,
            creation_timestamp: new Date().toISOString(),
            logical_operator: TriggerLogicalOperatorEnum.AND,
            comparison_operator: TriggerComparisonOperatorEnum.GT,
            comparison_value: undefined,
            trigger_id: triggerId,
            processing_order: (parentTrigger.trigger_rows?.length || 0) + 1,
            variable_id: undefined,
        };
        this.registerRowInputs(newRow);

        let newRows = [...(parentTrigger.trigger_rows || []), newRow];
        newRows = newRows.sort((a, b) => (a.processing_order ?? 0) - (b.processing_order ?? 0));

        const updatedTriggers = localTriggers.map(t =>
            t.id === triggerId
                ? {...t, trigger_rows: newRows}
                : t
        );

        this.setState(
            state => ({
                localTriggers: updatedTriggers,
                nextRowId: state.nextRowId + 1
            })
        );
    }

    /**
     * Updates fields of a specific row within a trigger.
     * @param triggerId ID of the parent trigger.
     * @param rowId ID of the row to update.
     * @param updatedFields Partial row fields to update.
     */

    @autobind
    updateRow(triggerId: string, rowId: string, updatedFields: TriggerRowInput) {
        const {localTriggers, rowInputStateIds} = this.state;
        const updatedTriggers = localTriggers.map(t =>
            t.id === triggerId
                ? {
                    ...t,
                    trigger_rows: t.trigger_rows.map(r =>
                        r.id === rowId ? {...r, ...updatedFields} : r
                    ),
                }
                : t
        );

        const updatedRow = updatedTriggers
            .find(t => t.id === triggerId)!
            .trigger_rows.find(r => r.id === rowId)!;

        const ids = rowInputStateIds[rowId];
        if (ids) {
            if ("variable_id" in updatedFields) {
                this.rowInputStateControl.updateValue(ids.variableId, updatedRow.variable_id || "");
            }
            if ("comparison_value" in updatedFields) {
                this.rowInputStateControl.updateValue(
                    ids.numberId,
                    updatedRow.comparison_value !== undefined
                        ? String(updatedRow.comparison_value)
                        : ""
                );
            }
        }

        this.setState({localTriggers: updatedTriggers});
    }

    /**
     * Attempts to save a row to the backend if all inputs are valid.
     * Handles both new rows and updates for existing rows.
     * @param triggerId ID of the parent trigger.
     * @param rowId ID of the row to save.
     */

    private trySaveRowToBackend(triggerId: string, rowId: string) {
        setTimeout(() => {
            const updatedRow = this.state.localTriggers
                .find(t => t.id === triggerId)!
                .trigger_rows.find(r => r.id === rowId)!;
            const ids = this.state.rowInputStateIds[rowId];
            const validVar = ids ? this.rowInputStateControl.getValidityForKey(rowId + "-variable") : true;
            const validNum = ids ? this.rowInputStateControl.getValidityForKey(rowId + "-number") : true;

            // Only save if all inputs are valid and required fields are set

            if (!validVar || !validNum ||
                !updatedRow.variable_id ||
                updatedRow.comparison_value === undefined ||
                updatedRow.comparison_value === null) {
                return;
            }
            if (rowId.startsWith("newrow-")) {
                const {id, ...rowWithoutId} = updatedRow;
                this.processFetchRequest(
                    getRequest_create_trigger_row(rowWithoutId as Omit<TriggerRow, "id">),
                    (createdRow) => {
                        const nextTriggers = this.state.localTriggers.map(t =>
                            t.id === triggerId
                                ? {...t, trigger_rows: t.trigger_rows.map(r => r.id === rowId ? createdRow : r)}
                                : t
                        );
                        this.setState({localTriggers: nextTriggers});
                        this.props.loadData([ApiEndPoints.TRIGGER]);
                        this.props.displayMessage?.(createSuccessFeedbackMessage(LabelTextMessageTypes.TRIGGER_UPDATED));
                    }
                );
            } else {
                this.sendUpdateRow(updatedRow);
            }
        }, 0);
    }

    /**
     * Handles dropping a variable onto a row to assign it.
     * @param e Drag event.
     * @param triggerId ID of the parent trigger.
     * @param rowId ID of the row where the variable is dropped.
     */

    @autobind
    handleRowDrop(e: React.DragEvent<HTMLTableCellElement>, triggerId: string, rowId: string) {
        e.preventDefault();
        const variableName = e.dataTransfer.getData("variable");

        if (!variableName) return;
        const variableObj = this.props.variables.find(v => v.name === variableName);
        if (!variableObj) return;

        const trigger = this.state.localTriggers.find(t => t.id === triggerId);
        const row = trigger?.trigger_rows.find(r => r.id === rowId);
        if (!row) return;

        this.updateRow(triggerId, rowId, {
            ...row,
            variable_id: variableObj.id
        });

        this.trySaveRowToBackend(triggerId, rowId);
    }

    /**
     * Handles drag start for a trigger card.
     * @param e Drag event.
     * @param triggerId ID of the trigger being dragged.
     */

    @autobind
    handleTriggerDragStart(e: React.DragEvent<HTMLDivElement>, triggerId: string) {
        e.dataTransfer.effectAllowed = "move";
        this.setState({draggedTriggerId: triggerId});
    }

    /**
     * Prevents default behavior for dragging over a trigger card.
     * @param e Drag event.
     */

    @autobind
    handleTriggerDragOver(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
    }

    /**
     * Moves a dragged trigger to a new position within the list.
     * Updates processing_order for all triggers.
     * @param draggedId ID of the dragged trigger.
     * @param targetTriggerId ID of the trigger to insert before/after.
     */

    private moveTrigger(draggedId: string, targetTriggerId: string) {
        const triggers = [...this.state.localTriggers];
        const draggedIndex = triggers.findIndex(t => t.id === draggedId);
        const targetIndex = triggers.findIndex(t => t.id === targetTriggerId);
        if (draggedIndex === -1 || targetIndex === -1) return;

        const [draggedTrigger] = triggers.splice(draggedIndex, 1);


        const insertAt = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;

        triggers.splice(insertAt, 0, draggedTrigger);

        triggers.forEach((t, i) => t.processing_order = i + 1);

        this.setState({localTriggers: triggers, draggedTriggerId: null});

        triggers.forEach(t => this.sendUpdateTrigger(t));
    }

    /**
     * Handles dropping a trigger card onto another trigger.
     * @param e Drag event.
     * @param targetTriggerId ID of the trigger being dropped onto.
     */

    @autobind
    handleTriggerDrop(e: React.DragEvent<HTMLDivElement>, targetTriggerId: string) {
        e.preventDefault();
        const {draggedTriggerId} = this.state;
        if (!draggedTriggerId || draggedTriggerId === targetTriggerId) {
            this.setState({draggedTriggerId: null});
            return;
        }
        this.moveTrigger(draggedTriggerId, targetTriggerId);
    }

    /**
     * Opens the modal to edit a trigger.
     * Initializes input state control for the modal fields.
     * @param trigger Trigger to edit.
     */

    @autobind
    openEditTriggerModal(trigger: Trigger) {
        this.setState({
            showEditTriggerModal: true,
            editingTrigger: trigger,
            editingTriggerValid: true,
        });
    }

    /**
     * Closes the edit trigger modal.
     */

    @autobind
    closeEditTriggerModal() {
        this.setState({
            showEditTriggerModal: false,
            editingTrigger: undefined,
            editingTriggerValid: true,
        });
    }

    /**
     * Handles submission from the edit trigger modal.
     * Updates trigger in local state and sends update to backend.
     * @param triggerInput Partial trigger data from modal.
     */

    @autobind
    handleTriggerEditModalSubmit(triggerInput: TriggerInput) {
        const {editingTrigger, localTriggers} = this.state;
        if (!editingTrigger) return;
        const updatedTrigger = {...editingTrigger, ...triggerInput};
        const updatedTriggers = localTriggers.map(t =>
            t.id === editingTrigger.id ? updatedTrigger : t
        );
        this.setState({localTriggers: updatedTriggers});
        this.sendUpdateTrigger(updatedTrigger);
        this.closeEditTriggerModal();
    }

    /**
     * Handles deletion of a trigger by id, calls API, reloads data, and shows feedback.
     */
    @autobind
    handleDeleteTrigger(triggerId: string) {
        this.processFetchRequest(
            getRequest_delete_trigger(triggerId),
            () => {
                this.props.loadData([ApiEndPoints.TRIGGER, ApiEndPoints.VARIABLE]);
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.TRIGGER_DELETED));
            }
        );
    }

    /**
     * Handles deletion of a trigger row.
     * @param triggerId ID of the parent trigger.
     * @param rowId ID of the row to delete.
     */

    @autobind
    handleDeleteTriggerRow(triggerId: string, rowId: string) {
        this.processFetchRequest(
            getRequest_delete_trigger_row(rowId),
            () => {
                const {localTriggers} = this.state;
                const updatedTriggers = localTriggers.map(t =>
                    t.id === triggerId
                        ? {
                            ...t,
                            trigger_rows: t.trigger_rows.filter(r => r.id !== rowId),
                        }
                        : t
                );
                this.setState({localTriggers: updatedTriggers});
                this.props.loadData([ApiEndPoints.TRIGGER]);
                this.props.displayMessage?.(createSuccessFeedbackMessage(LabelTextMessageTypes.TRIGGER_UPDATED));
            }
        );
    }

    /**
     * Renders the list of triggers and their rows.
     * Includes buttons for adding triggers/rows and editing triggers.
     */

    render(): ReactElement {
        const {localTriggers, rowInputStateIds} = this.state;
        const sortedTriggers = Array.from(
            localTriggers
        ).sort((a, b) => (a.processing_order ?? 0) - (b.processing_order ?? 0));

        return (
            <div className={styles.tabContainer}>
                <div className={styles.scrollArea}>
                    <Row>
                        <Col xs={12}>
                            <div className={styles.cardStack}>
                                {sortedTriggers.map((trigger) => (
                                    <div
                                        key={trigger.id}
                                        onDragOver={e => this.handleTriggerDragOver(e)}
                                        onDrop={e => this.handleTriggerDrop(e, trigger.id)}
                                    >
                                        <RBSCard
                                            header={
                                                <TabCardHeader
                                                    icon={"⏱️"}
                                                    title={
                                                        <span className={styles.cardHeaderTitleWrap}>
                                                            <span className={styles.cardHeaderTitle}>{trigger.name}</span>
                                                            <span className={styles.highlightTag}>[{trigger.trigger_type}]</span>
                                                        </span>
                                                    }
                                                    actions={
                                                        <div className={styles.headerActions}>
                                                            <RBSButton
                                                                label="Add Row"
                                                                variant={RBSButtonVariants.PRIMARY}
                                                                size={RBSButtonSizes.SMALL}
                                                                onClick={() => this.handleAddTriggerRow(trigger.id)}
                                                            />
                                                            <RBSButton
                                                                label="Edit"
                                                                variant={RBSButtonVariants.SECONDARY}
                                                                size={RBSButtonSizes.SMALL}
                                                                onClick={() => this.openEditTriggerModal(trigger)}
                                                            />
                                                            <RBSButton
                                                                label="Delete"
                                                                variant={RBSButtonVariants.DANGER}
                                                                size={RBSButtonSizes.SMALL}
                                                                onClick={() => this.handleDeleteTrigger(trigger.id)}
                                                            />
                                                        </div>
                                                    }
                                                    draggable
                                                    onDragStart={e => this.handleTriggerDragStart(e, trigger.id)}
                                                />
                                            }
                                            background={RBSCardVariants.Light}
                                            border={RBSCardVariants.Primary}
                                            header_class_name={styles.cardHeaderPrimary}
                                            body_class_name={styles.cardBodyPanel}
                                            container_class_name={styles.roundedCard}
                                        >
                                            <table className={styles.dataTable}>
                                                <thead>
                                                <tr>
                                                    <th className={`${styles.headerPadLg} ${styles.colNarrowSm}`}>Logical</th>
                                                    <th className={`${styles.headerPadLg} ${styles.colWide}`}>Variable</th>
                                                    <th className={`${styles.headerPadLg} ${styles.colNarrowSm}`}>Operator</th>
                                                    <th className={`${styles.headerPadLg} ${styles.colNarrowSm}`}>Value</th>
                                                    <th className={`${styles.headerPadLg} ${styles.bracketCell}`}></th>
                                                    <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}></th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {(trigger.trigger_rows
                                                        ? trigger.trigger_rows.slice().sort((a, b) => (a.processing_order ?? 0) - (b.processing_order ?? 0))
                                                        : []
                                                ).map((row, idx, arr) => {
                                                    const isFirst = idx === 0;
                                                    const isLast = idx === arr.length - 1;
                                                    const multipleRows = arr.length > 1;
                                                    const variableObj = row.variable_id
                                                        ? this.props.variables.find(v => v.id === row.variable_id)
                                                        : undefined;

                                                    const ids = rowInputStateIds[row.id] || {
                                                        variableId: -1,
                                                        numberId: -1
                                                    };
                                                    const validVar = ids.variableId !== -1 ? this.rowInputStateControl.getValidityForKey(row.id + "-variable") : true;
                                                    const validNum = ids.numberId !== -1 ? this.rowInputStateControl.getValidityForKey(row.id + "-number") : true;

                                                    return (
                                                        <tr
                                                            key={row.id}
                                                            className={this.state.hoveredRow === row.id ? styles.tableRowHover : ''}
                                                            onMouseEnter={() => this.setState({hoveredRow: row.id})}
                                                            onMouseLeave={() => this.setState({hoveredRow: undefined})}
                                                        >
                                                            <td className={`${styles.cellPadLg} ${styles.colNarrowSm}`}>
                                                                {multipleRows && isFirst ? (
                                                                    <span className="fw-bold">{"("}</span>
                                                                ) : !multipleRows ? (
                                                                    <span className="fw-bold">{""}</span>
                                                                ) : (
                                                                    <select
                                                                        className="form-select"
                                                                        value={row.logical_operator}
                                                                        onChange={e => {
                                                                            this.updateRow(trigger.id, row.id, {
                                                                                ...row,
                                                                                logical_operator: e.target.value as TriggerLogicalOperatorEnum
                                                                            });
                                                                            this.trySaveRowToBackend(trigger.id, row.id);
                                                                        }}
                                                                    >
                                                                        {[TriggerLogicalOperatorEnum.AND, TriggerLogicalOperatorEnum.OR_BRACKETS]
                                                                            .map(op => (
                                                                                <option key={op} value={op}>
                                                                                    {logicalOperatorLabelMap[op]}
                                                                                </option>
                                                                            ))
                                                                        }
                                                                    </select>
                                                                )}
                                                            </td>
                                                            <VariableDropCell
                                                                user_language={this.props.user_language}
                                                                onDrop={e => this.handleRowDrop(e, trigger.id, row.id)}
                                                                isRequiredMissing={validVar === false}
                                                                isLastRow={isLast}
                                                                minWidthPx={120}
                                                            >
                                                                {variableObj ? (
                                                                    <VariablesBadges
                                                                        variables={[variableObj]}
                                                                        draggable
                                                                        onDragStart={(v, e) => {
                                                                            e.dataTransfer.setData("variable", v.name);
                                                                            e.dataTransfer.setData("from", "TriggersTab");
                                                                            e.dataTransfer.setData("fromTriggerId", trigger.id);
                                                                            this.setState({
                                                                                draggedRow: {
                                                                                    triggerId: trigger.id,
                                                                                    rowId: row.id,
                                                                                    variableName: v.name
                                                                                }
                                                                            });
                                                                        }}
                                                                    />
                                                                ) : undefined}
                                                            </VariableDropCell>
                                                            <td className={`${styles.cellPadLg} ${styles.colNarrowSm}`}>
                                                                <select
                                                                    className="form-select"
                                                                    value={row.comparison_operator}
                                                                    onChange={e => {
                                                                        this.updateRow(trigger.id, row.id, {
                                                                            ...row,
                                                                            comparison_operator: e.target.value as TriggerComparisonOperatorEnum
                                                                        });
                                                                        this.trySaveRowToBackend(trigger.id, row.id);
                                                                    }}
                                                                >
                                                                    {Object.entries(operatorSymbolMap).map(([key, symbol]) => (
                                                                        <option key={key}
                                                                                value={key}>{symbol}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td className={`${styles.cellPadLg} ${styles.colNarrowSm}`}>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control form-control-sm no-spin${validNum === false ? " is-invalid" : ""}`}
                                                                    value={
                                                                        row.comparison_value === undefined || row.comparison_value === null
                                                                            ? ""
                                                                            : row.comparison_value
                                                                    }
                                                                    step="any"
                                                                    onChange={e => {
                                                                        const value = e.target.value === "" ? undefined : parseFloat(e.target.value);
                                                                        this.updateRow(trigger.id, row.id, {
                                                                            ...row,
                                                                            comparison_value: value
                                                                        });
                                                                    }}
                                                                    onBlur={() => {
                                                                        this.trySaveRowToBackend(trigger.id, row.id);
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className={`${styles.cellPadLg} ${styles.bracketCell}`}>
                                                                {multipleRows && isLast ? ")" : ""}
                                                            </td>
                                                            <td className={`${styles.cellPadLg} ${styles.actionCell}`}>
                                                                <div className={styles.hoverButtons}>
                                                                    <RBSButton
                                                                        label="🗑️"
                                                                        title="Delete"
                                                                        onClick={() => this.handleDeleteTriggerRow(trigger.id, row.id)}
                                                                        variant={RBSButtonVariants.DANGER}
                                                                        size={RBSButtonSizes.SMALL}
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {(!trigger.trigger_rows || trigger.trigger_rows.length === 0) && (
                                                    <EmptyStateRow
                                                        inTable
                                                        colSpan={6}
                                                        message={'No rows yet. Use "Add Row" or drop a variable.'}
                                                    />
                                                )}
                                                </tbody>
                                            </table>
                                        </RBSCard>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                    <div className={styles.toolbarBottom}>
                        <RBSButton
                            label="Add Trigger"
                            variant={RBSButtonVariants.PRIMARY}
                            size={RBSButtonSizes.LARGE}
                            onClick={this.handleAddTrigger}
                            title="Add Trigger"
                        />
                    </div>

                    {this.state.editingTrigger && <TriggerEditModal
                        show={this.state.showEditTriggerModal}
                        trigger={this.state.editingTrigger}
                        onSubmit={this.handleTriggerEditModalSubmit}
                        onClose={this.closeEditTriggerModal}
                        isValid={this.state.editingTriggerValid}
                        inputStateControl={this.editingTriggerInputControl}
                        user_language={this.props.user_language}
                    />}
                </div>
            </div>
        );
    }
}
